import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { IframeService } from '@app/service/iframe/iframe.service';
import { StateService } from '@app/service/state/state.service';
import {
  KreditzFormStatusResponse,
  UserService,
} from '@app/service/user-service/user.service';
import { SeRoute } from '@assets/val/route-constants';
import { LoggingService } from '@app/service/logging-service/logging.service';
import {
  Data,
  EventMessage,
} from '@app/component/kreditz-iframe/kreditz-iframe.component';
import { EMPTY, timer, Subject } from 'rxjs';
import { switchMap, takeWhile, takeUntil, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-kreditz',
  templateUrl: './kreditz.component.html',
  styleUrls: ['../../page-shared.css', './kreditz.component.scss'],
})
export class KreditzComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  loading: boolean = true;
  error: boolean = false;
  navigationInProgress: boolean = false;

  iframeUrl: SafeResourceUrl;
  isIframe: boolean;

  private destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,

    private loggingService: LoggingService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    private iframeService: IframeService
  ) {
    super(router, route, stateService, headerService);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.setup();
  }

  async setup(): Promise<void> {
    this.isIframe = this.iframeService.isThisInIframe();

    this.fetchKreditzUrl();
    this.loading = false;
  }

  fetchKreditzUrl(): void {
    try {
      const activeLocale: string = this.stateService.getCurrentLocale();

      this.userService
        .getKreditzAccountCheckUrl('kreditz', activeLocale)
        .subscribe(
          (res) => {
            if (!res || !res.url) {
              this.error = true;
            } else {
              const url = this.sanitizer.bypassSecurityTrustResourceUrl(
                res.url
              );
              this.iframeUrl = url;
            }
          },
          (err) => {
            this.loggingService.error(
              `Failed to fetch Kreditz URL: ${JSON.stringify(err)}`
            );
            this.error = true;
          }
        );
    } catch (err) {
      this.loggingService.error(
        `Failed to fetch Kreditz URL: ${JSON.stringify(err)}`
      );
      this.error = true;
    }
  }

  async onEventData(eventData: Data): Promise<void> {
    if (eventData.message === EventMessage.COMPLETED) {
      await this.updateStatus(eventData.case_id, eventData.message);
    } else if (eventData.message === EventMessage.ERROR) {
      this.loggingService.error(
        `Kreditz failed and responded with: ${JSON.stringify(eventData)}`
      );
      this.error = true;
    } else if (eventData.message === EventMessage.USER_CANCELLED) {
      this.navigateTo(SeRoute.PRECHECK);
    } else {
      this.loggingService.error(
        `Received unknown Kreditz event data: ${JSON.stringify(eventData)}`
      );
      this.error = true;
    }
  }

  updateStatus(caseId: string, status: EventMessage): void {
    timer(0, 2000)
      .pipe(
        switchMap(() =>
          this.userService.updateKreditzAccountCheckStatus(caseId, status)
        ),
        takeWhile(
          (response: KreditzFormStatusResponse) =>
            !response.success && !response.error,
          true
        ),
        takeUntil(timer(300000)),
        takeUntil(this.destroy$),
        catchError((err) => {
          this.loggingService.error(
            `Failed to fetch Kreditz status: ${JSON.stringify(err)}`
          );
          this.error = true;
          return EMPTY;
        })
      )
      .subscribe((response) => {
        if (response.success) {
          this.navigate();
        } else if (response.error) {
          this.error = true;
        }
      });
  }

  handleCtaClick(): void {
    this.navigateTo(SeRoute.PRECHECK);
  }

  navigateTo(routeName: string): void {
    this.navigationInProgress = true;

    this.router.navigate([routeName], {
      queryParamsHandling: 'merge',
    });

    this.navigationInProgress = false;
  }
}
